<template>
  <div class="login-callback-container">
    <el-card class="callback-card" v-loading="loading">
      <template #header>
        <div class="card-header">
          <el-icon v-if="loading"><Loading class="is-loading" /></el-icon>
          <span>{{ statusText }}</span>
        </div>
      </template>

      <div class="callback-content">
        <!-- 加载中 -->
        <div v-if="loading" class="status-content">
          <el-icon class="status-icon"><Loading class="is-loading" /></el-icon>
          <p>正在处理登录回调...</p>
        </div>

        <!-- 成功 -->
        <div v-else-if="success" class="status-content success">
          <el-icon class="status-icon"><CircleCheck /></el-icon>
          <p>登录成功！</p>
          <p class="sub-text">正在跳转...</p>
        </div>

        <!-- 错误 -->
        <div v-else-if="error" class="status-content error">
          <el-icon class="status-icon"><CircleClose /></el-icon>
          <p>{{ error }}</p>
          <el-button type="primary" @click="handleRetry">重试</el-button>
          <el-button @click="handleGoToLogin">返回登录</el-button>
        </div>
      </div>
    </el-card>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, nextTick, unref } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { ElCard, ElButton, ElIcon } from 'element-plus';
import { Loading, CircleCheck, CircleClose } from '@element-plus/icons-vue';
import { useAuthStore } from '@/stores/useAuthStore';
import { usePermissionStore } from '@/stores/usePermissionStore';
import * as authApi from '@/api/authApi';
import type { FeishuOAuthResponse } from '@/api/authApi';
import { promptAccountMergeIfPending } from '@/utils/accountMergePrompt';
import { resolveLoginSuccessRedirect } from '@/utils/postLoginRedirect';

function resolveCallbackRedirect(userInfo: unknown): string {
  return resolveLoginSuccessRedirect(route.query.redirect, userInfo as { postLoginRedirect?: string });
}

const route = useRoute();
const router = useRouter();
const authStore = useAuthStore();
const permissionStore = usePermissionStore();

const loading = ref(true);
const success = ref(false);
const error = ref<string | null>(null);

const statusText = computed(() => {
  if (loading.value) return '处理中';
  if (success.value) return '登录成功';
  if (error.value) return '登录失败';
  return '处理中';
});

onMounted(async () => {
  await handleCallback();
});

function firstQuery(v: string | string[] | undefined | null): string | undefined {
  if (v == null) return undefined;
  return Array.isArray(v) ? v[0] : v;
}

/** 飞书工作台 302 落地后，轮询可能略早于 Redis 写入，短暂重试 */
async function pollFeishuLoginWithRetry(state: string): Promise<FeishuOAuthResponse | null> {
  const attempts = 20;
  const delayMs = 400;
  for (let i = 0; i < attempts; i++) {
    const result = await authApi.pollFeishuLoginResult(state);
    if (result) return result;
    await new Promise((r) => setTimeout(r, delayMs));
  }
  return null;
}

async function handleCallback() {
  loading.value = true;
  error.value = null;
  success.value = false;

  try {
    const code = firstQuery(route.query.code as string | string[] | undefined);
    const state = firstQuery(route.query.state as string | string[] | undefined);
    const corpId = firstQuery(route.query.corpId as string | string[] | undefined);
    const routeName = route.name?.toString();

    // 必须先清理旧会话再写入新 token：若在 setAuth 之后调用 clearCache，会执行 authStore.setRoles([])，
    // 清空飞书回调刚写入的角色，随后若权限接口失败或偏慢，路由守卫会误判「未配置」并反复跳转（工作台 WebView 表现为闪退）。
    permissionStore.clearCache();

    // 飞书桌面端：仅 state（后端已处理 code，重定向到此页供前端轮询）
    if (routeName === 'FeishuCallback' && state && !code) {
      const result = await pollFeishuLoginWithRetry(state);
      if (result) {
        authStore.setAuth(result, { remember: true });
      } else {
        throw new Error('登录结果已过期，请重新打开应用');
      }
    } else if (!code || !state) {
      throw new Error('缺少必要的参数：code 或 state');
    } else if (routeName === 'FeishuCallback') {
      await authStore.handleFeishuCallback(code, state);
    } else {
      await authStore.handleLoginCallback(code, state, corpId);
    }

    success.value = true;
    await nextTick();
    await permissionStore.fetchPermissions();

    const redirect = resolveCallbackRedirect(unref(authStore.userInfo));
    await new Promise<void>((resolve) => {
      setTimeout(resolve, 800);
    });
    await promptAccountMergeIfPending(router, redirect, unref(authStore.userInfo));
  } catch (err: any) {
    error.value = err.message || '登录回调处理失败';
    console.error('登录回调处理失败:', err);
  } finally {
    loading.value = false;
  }
}

function handleRetry() {
  handleCallback();
}

function handleGoToLogin() {
  router.push('/login');
}
</script>

<style scoped lang="scss">
.login-callback-container {
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  padding: 20px;
}

.callback-card {
  width: 100%;
  max-width: 500px;
  
  .card-header {
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 18px;
    font-weight: 500;
  }
}

.callback-content {
  min-height: 200px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px 20px;
}

.status-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
  text-align: center;
  
  .status-icon {
    font-size: 64px;
    margin-bottom: 8px;
  }
  
  p {
    margin: 0;
    font-size: 16px;
    color: #606266;
  }
  
  .sub-text {
    font-size: 14px;
    color: #909399;
  }
  
  &.success {
    .status-icon {
      color: #67c23a;
    }
    
    p {
      color: #67c23a;
      font-weight: 500;
    }
  }
  
  &.error {
    .status-icon {
      color: #f56c6c;
    }
    
    p {
      color: #f56c6c;
    }
  }
}
</style>
